import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { NotificationBell } from "@/components/NotificationBell";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { disconnectPush } from "@/lib/push";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, CalendarDays, Repeat, BarChart3, Users,
  CalendarRange, LayoutGrid, Scale, FileSpreadsheet, ShieldCheck, ScrollText,
  Settings, LogOut, Menu, PartyPopper, ChevronDown, User, ScanLine, CalendarCheck, ListChecks, Megaphone, SlidersHorizontal, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";

const NAV = {
  volunteer: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/availability", label: "Beschikbaarheid", icon: CalendarDays },
    { to: "/team", label: "Rooster", icon: CalendarRange },
    { to: "/activities", label: "Activiteiten", icon: Megaphone },
    { to: "/preferences", label: "Mijn voorkeuren", icon: SlidersHorizontal },
    { to: "/swaps", label: "Ruilverzoeken", icon: Repeat },
    { to: "/stats", label: "Mijn statistieken", icon: BarChart3 },
  ],
  planner: [
    { to: "/planner", label: "Planner dashboard", icon: LayoutDashboard },
    { to: "/board", label: "Planbord", icon: LayoutGrid },
    { to: "/services", label: "Diensten", icon: CalendarDays },
    { to: "/availability", label: "Mijn beschikbaarheid", icon: CalendarCheck },
    { to: "/volunteers", label: "Vrijwilligers", icon: Users },
    { to: "/availability-status", label: "Beschikbaarheidsstatus", icon: ListChecks },
    { to: "/distribution", label: "Verdeling & statistiek", icon: Scale },
    { to: "/planner-swaps", label: "Ruilverzoeken", icon: Repeat },
    { to: "/attendance", label: "Aanwezigheid", icon: ScanLine },
    { to: "/activities", label: "Activiteiten", icon: Megaphone },
    { to: "/import-export", label: "Import / Export", icon: FileSpreadsheet },
  ],
  admin: [
    { to: "/admin/users", label: "Gebruikers & rollen", icon: ShieldCheck },
    { to: "/admin/audit", label: "Auditlog", icon: ScrollText },
    { to: "/admin/settings", label: "Instellingen", icon: Settings },
  ],
};

function navFor(role) {
  if (role === "volunteer") return [{ items: NAV.volunteer }];
  if (role === "planner") return [{ items: NAV.planner }];
  if (role === "admin")
    return [
      { label: "Planning", items: NAV.planner },
      { label: "Beheer", items: NAV.admin },
    ];
  return [];
}

function NavContent({ role, onNavigate }) {
  const groups = navFor(role);
  return (
    <nav className="flex flex-col gap-6 py-4">
      {groups.map((g, i) => (
        <div key={i}>
          {g.label && (
            <p className="px-4 mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              {g.label}
            </p>
          )}
          <div className="flex flex-col gap-1 px-2">
            {g.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                data-testid={`nav-${item.to.replace(/\//g, "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-red-600 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <item.icon className="w-4.5 h-4.5 shrink-0" style={{ width: 18, height: 18 }} />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

const ROLE_LABEL = { volunteer: "Vrijwilliger", planner: "Planner", admin: "Beheerder" };

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { sidebarHidden, toggleSidebar } = useUI();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);


  const doLogout = async () => {
    try { await disconnectPush(); }
    catch (_) { toast.warning("Push uitschakelen is niet gelukt. Blokkeer meldingen in je browser op een gedeeld apparaat."); }
    await logout();
    navigate("/login");
  };

  const Brand = (
    <div className="flex items-center gap-2.5 px-4 h-16 border-b border-slate-800">
      <img
        src="/kelderke-logo.png"
        alt="'t Kelderke"
        className="w-9 h-9 object-contain shrink-0"
      />
      <div className="leading-tight">
        <p className="font-heading font-bold text-white text-sm">Bar 't Kelderke</p>
        <p className="text-[11px] text-slate-400">Goirle</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      {!sidebarHidden && (
      <aside className="hidden lg:flex flex-col w-64 bg-slate-900 shrink-0 fixed inset-y-0 left-0 z-30">
        {Brand}
        <div className="flex-1 overflow-y-auto">
          <NavContent role={user?.role} />
        </div>
      </aside>
      )}

      <div className={`flex-1 ${sidebarHidden ? "" : "lg:ml-64"} flex flex-col min-w-0`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 h-16 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 min-w-0">
          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-slate-900 border-slate-800">
              {Brand}
              <NavContent role={user?.role} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>


          <Button variant="ghost" size="icon" className="hidden lg:inline-flex" onClick={toggleSidebar} aria-label={sidebarHidden ? "Menu tonen" : "Menu verbergen"} data-testid="toggle-sidebar-btn">
            {sidebarHidden ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </Button>
          <div className="flex-1" />
          <RoleSwitcher />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2 shrink-0" data-testid="profile-menu">
                <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <User className="w-4 h-4 text-red-700 dark:text-red-300" />
                </div>
                <div className="hidden sm:block text-left leading-tight">
                  <p className="text-sm font-medium max-w-32 truncate">{user?.name}</p>
                  <p className="text-[11px] text-muted-foreground">{ROLE_LABEL[user?.role]}</p>
                </div>
                <ChevronDown className="hidden sm:block w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <RoleSwitcher banner />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1500px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
